import { Box, Typography, keyframes, TextField, Button, useMediaQuery, useTheme, Container, Accordion, AccordionSummary, AccordionDetails, Grid, Card, CardContent, CardMedia } from "@mui/material";
import { useState, useEffect, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import logo from "../img/screamifyweblogo.png";
import backdrop from "../img/backdrop.png";
import favicon from "../img/Favicon-01.png";
import iosBadge from "../img/ios-badge.svg";
import googlePlayBadge from "../img/googleplay-badge.png";
import appHands from "../img/app-hands.png";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuthService } from '../services/authService';
import { useLocation } from 'react-router-dom';
// tracking removed
import device1 from "../img/device-1.jpg";
import device2 from "../img/device-2.jpg";
import device3 from "../img/device-3.jpg";
import device4 from "../img/device-4.jpg";
import device5 from "../img/device-5.jpg";
import device6 from "../img/device-6.jpg";

// Hardcoded Airtable credentials
const AIRTABLE_API_KEY = 'pat4FML0uvc5EtZiq.4b9c4db63db5d87249782d8456c6341da6a25cc41041487340138cd1d4453a99';
const AIRTABLE_BASE_ID = 'appEp9ztiYve51qqI';
const AIRTABLE_TABLE_NAME = 'Table%201';

// Airtable API info for blog posts
const BLOG_AIRTABLE_API_KEY = 'pat4FML0uvc5EtZiq.4b9c4db63db5d87249782d8456c6341da6a25cc41041487340138cd1d4453a99';
const BLOG_AIRTABLE_BASE_ID = 'appttt0VypJe8DyRL';
const BLOG_AIRTABLE_TABLE_NAME = 'Posts';

// DynamoDB API endpoint for blog posts
const DYNAMODB_API_ENDPOINT = 'https://ke5sqc1hgc.execute-api.us-east-1.amazonaws.com/get-blog';

const pulse = keyframes`
  0% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.02);
    filter: brightness(1.2);
  }
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// FAQ data
const faqs = [
  {
    question: "What is Screamify™?",
    answer: "Screamify™ is a revolutionary streaming platform dedicated exclusively to independent horror content. We're not just another streaming service – we're a community-driven platform that funds, promotes, and celebrates independent horror filmmaking."
  },
  {
    question: "How much does a subscription cost?",
    answer: "Screamify™ is committed to keeping horror accessible. Our subscription is just $2.99 per month, making it one of the most affordable streaming services available. This price point allows us to maintain a sustainable platform while ensuring most of our revenue goes toward supporting independent filmmakers."
  },
  {
    question: "What makes Screamify™ different from other streaming platforms?",
    answer: "Unlike traditional streaming platforms, Screamify™ reinvests a significant portion of subscription revenue directly into independent horror film production. We also host an annual film festival and actively seek out undiscovered horror talent. Additionally, we produce and distribute horror, true crime, and paranormal podcasts, expanding our content beyond just films. Our focus is solely on horror and related genres, allowing us to build a dedicated community of genre enthusiasts and creators."
  }
];

// Features data
const features = [
  {
    icon: <Box component="img" src={favicon} alt="Screamify Icon" sx={{ width: 40, height: 40 }} />,
    title: "Exclusive Horror Content",
    description: "Access a curated collection of independent horror films, from psychological thrillers to supernatural scares."
  },
  {
    icon: <Box component="img" src={favicon} alt="Screamify Icon" sx={{ width: 40, height: 40 }} />,
    title: "Community Reviews",
    description: "Share your thoughts, read reviews, and discover new horror gems recommended by fellow enthusiasts."
  },
  {
    icon: <Box component="img" src={favicon} alt="Screamify Icon" sx={{ width: 40, height: 40 }} />,
    title: "Film Festival",
    description: "Participate in our annual horror film festival showcasing emerging talent and innovative storytelling."
  }
];

// Enhanced structured data for search engines
const structuredData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Screamify™ - Ultimate Horror Streaming Platform",
  "alternateName": "Screamify - Independent Horror Community",
  "description": "Discover the best independent horror films, psychological thrillers, and supernatural horror. Join our community of horror fans and support indie horror creators.",
  "url": "https://www.screamify.com",
  "potentialAction": [
    {
      "@type": "JoinAction",
      "target": "https://www.screamify.com",
      "description": "Join Screamify's horror streaming community"
    },
    {
      "@type": "SearchAction",
      "target": "https://www.screamify.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  ],
  "genre": ["Horror", "Thriller", "Independent Film", "Supernatural Horror", "Psychological Horror", "Gore", "Slasher"],
  "keywords": "horror streaming, indie horror films, horror community, horror reviews, horror content creators, supernatural horror, psychological horror, slasher films, horror anthology, horror movies, thriller movies, horror directors, indie filmmakers, horror platform",
  "audience": {
    "@type": "Audience",
    "audienceType": "Horror fans"
  },
  "foundingDate": "2023"
};

// Blog structured data (empty by default, filled when posts are loaded)
const blogStructuredData = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Screamify Horror Blog",
  "description": "Insights, reviews, and analysis of indie horror cinema, horror film festivals, and horror industry trends",
  "url": "https://www.screamify.com/blog",
  "keywords": "horror blog, horror movie reviews, indie horror films, horror film analysis, horror industry trends, horror film festivals, horror directors, supernatural horror reviews, psychological horror analysis, slasher films, horror anthology",
  "blogPost": []
};

// FAQ structured data
const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

// Organization structured data
const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Screamify",
  "url": "https://www.screamify.com",
  "logo": "https://www.screamify.com/logo.png",
  "sameAs": [
    "https://twitter.com/screamify",
    "https://www.facebook.com/screamify",
    "https://www.instagram.com/screamify"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "",
    "contactType": "customer service",
    "email": "info@screamify.com",
    "availableLanguage": "English"
  }
};

function LandingPage() {
  const location = useLocation();
  const hasTracked = useRef(new Set());

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { isAuthenticated, getToken, clearExistingAuth } = useAuthService();

  useEffect(() => {
    // Add structured data to the page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
    
    // Add FAQ structured data
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify(faqStructuredData);
    document.head.appendChild(faqScript);
    
    // Add Organization structured data
    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.text = JSON.stringify(organizationStructuredData);
    document.head.appendChild(orgScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
      document.head.removeChild(orgScript);
    };
  }, []);

  // Add blog structured data when blog posts are loaded
  useEffect(() => {
    if (blogPosts.length > 0) {
      const blogData = { ...blogStructuredData };
      
      // Add each blog post to the structured data
      blogData.blogPost = blogPosts.map(post => {
        if (!post || !post.fields) return null;
        
        return {
          "@type": "BlogPosting",
          "headline": post.fields.Title || 'Horror Blog Post',
          "datePublished": post.fields.Date || new Date().toISOString(),
          "dateModified": post.fields.UpdatedDate || post.fields.Date || new Date().toISOString(),
          "url": `https://www.screamify.com/blog/${post.fields.BlogId || ''}`,
          "author": {
            "@type": "Person",
            "name": post.fields.Author || 'Screamify Team'
          },
          "publisher": {
            "@type": "Organization",
            "name": "Screamify",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.screamify.com/Favicon-01.png"
            }
          },
          "image": post.fields.Pic1Url || 'https://www.screamify.com/social-share.png',
          "articleSection": post.fields.Category || "Horror",
          "keywords": `horror, ${post.fields.Category ? post.fields.Category.toLowerCase() : 'horror'}, movie reviews, indie horror`,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://www.screamify.com/blog/${post.fields.BlogId || ''}`
          }
        };
      }).filter(Boolean);
      
      const blogScript = document.createElement('script');
      blogScript.type = 'application/ld+json';
      blogScript.text = JSON.stringify(blogData);
      blogScript.id = 'blog-structured-data';
      
      const existingScript = document.getElementById('blog-structured-data');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
      
      document.head.appendChild(blogScript);
    }
  }, [blogPosts]);

  // Check if the user is authenticated and their token is valid before redirecting
  useEffect(() => {
    const validateAuthAndRedirect = async () => {
      if (isAuthenticated) {
        try {
          const token = await getToken();
          if (token) {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              try {
                const payload = JSON.parse(atob(tokenParts[1]));
                const now = Math.floor(Date.now() / 1000);
                if (payload.exp && payload.exp > now) {
                  navigate("/screamify-central");
                } else {
                  await clearExistingAuth();
                }
              } catch (error) {
                await clearExistingAuth();
              }
            } else {
              await clearExistingAuth();
            }
          } else {
            await clearExistingAuth();
          }
        } catch (error) {
          await clearExistingAuth();
        }
      }
    };
    
    validateAuthAndRedirect();
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    
    setLoading(false);
    
    // Check authentication status and validate token before redirecting
    if (isAuthenticated) {
      try {
        const token = await getToken();
        if (token) {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const now = Math.floor(Date.now() / 1000);
            if (payload.exp && payload.exp > now) {
              navigate("/screamify-central");
              return;
            } else {
              await clearExistingAuth();
            }
          } else {
            await clearExistingAuth();
          }
        } else {
          await clearExistingAuth();
        }
      } catch (error) {
        await clearExistingAuth();
      }
    }
    
    // Navigate to Join.jsx instead of login
    navigate(`/join?email=${encodeURIComponent(email)}`);
  };

  // Fetch latest blog posts
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setBlogLoading(true);
        const url = `${DYNAMODB_API_ENDPOINT}?limit=3`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }
        const data = await response.json();
        if (data.blogs && data.blogs.length > 0) {
                     // Convert DynamoDB format to match existing component expectations
           const convertedPosts = data.blogs.map(blog => ({
             id: blog.SK,
             fields: {
               Title: blog.title,
               Content: blog.content,
               Category: blog.category,
               Author: blog.author,
               Date: blog.date,
               BlogId: encodeURIComponent(blog.SK), // URL encode SK since it contains # character
               Pic1Url: blog.pic1Url,
               Pic2Url: blog.pic2Url,
               Pic3Url: blog.pic3Url,
               HorrorTreeUrl: blog.horrorTreeUrl
             }
           }));
          setBlogPosts(convertedPosts);
        } else {
          setBlogPosts([]);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setBlogPosts([]);
      } finally {
        setBlogLoading(false);
      }
    };
    
    fetchBlogPosts();
  }, []);

  // Strip markdown syntax for previews
  const stripMarkdown = (text) => {
    if (!text) return '';
    
    return text
      // Remove headers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold and italic
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove inline code
      .replace(/`([^`]+)`/g, '$1')
      // Remove code blocks
      .replace(/```[\s\S]*?```/g, '')
      // Remove blockquotes
      .replace(/^>\s+/gm, '')
      // Remove list markers
      .replace(/^[\s]*[-*+]\s+/gm, '')
      .replace(/^[\s]*\d+\.\s+/gm, '')
      // Remove horizontal rules
      .replace(/^---+$/gm, '')
      // Remove extra whitespace and newlines
      .replace(/\n\s*\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <HelmetProvider>
      <>
        <Helmet>
          <title>Screamify™ - Independent Horror Streaming Platform | Join the Horror Revolution</title>
          <meta name="description" content="Join Screamify, the ultimate platform for horror enthusiasts. Stream exclusive indie horror films, write reviews, and connect with a community of horror fans. Starting at just $3.99/month." />
          <meta name="keywords" content="horror streaming, indie horror films, horror community, horror reviews, independent horror, horror platform, horror movies, thriller movies, horror blog, psychological horror, supernatural horror, slasher films, indie filmmakers" />
          <meta property="og:title" content="Screamify™ - Independent Horror Streaming Platform | Join the Horror Revolution" />
          <meta property="og:description" content="Join the ultimate platform for horror content. Stream exclusive indie horror films and connect with fellow horror enthusiasts. From $3.99/month." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.screamify.com" />
          <meta property="og:image" content="https://www.screamify.com/social-share.png" />
          <meta property="og:image:alt" content="Screamify Horror Streaming Platform Logo" />
          <meta property="og:site_name" content="Screamify" />
          <meta property="og:locale" content="en_US" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Screamify™ - Horror Streaming Platform | Indie Horror Films" />
          <meta name="twitter:description" content="Join the ultimate platform for horror content. Stream exclusive indie horror films and connect with fellow horror enthusiasts." />
          <meta name="twitter:image" content="https://www.screamify.com/social-share.png" />
          <meta name="twitter:site" content="@screamify" />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <link rel="canonical" href="https://www.screamify.com" />
          <meta name="author" content="Screamify Team" />
          <meta name="revisit-after" content="7 days" />
          <meta name="geo.placename" content="United States" />
          <meta name="theme-color" content="#ff0000" />
        </Helmet>

        <Box 
          component="main"
          sx={{ 
            bgcolor: '#000000',
            minHeight: '100vh',
            width: '100%',
            position: 'relative',
            backgroundImage: `url(${backdrop})`,
            backgroundSize: '100%',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              zIndex: 0,
            }
          }}
        >
          <Header />

          {/* Hero Section */}
          <Container maxWidth="xl">
            <Box
              sx={{
                pt: { xs: 16, md: 20 },
                pb: { xs: 8, md: 12 },
                position: 'relative',
                zIndex: 1,
                textAlign: 'center'
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Screamify - Horror Streaming Platform Logo"
                loading="eager"
                sx={{
                  width: { xs: '140px', sm: '160px', md: '200px' },
                  height: 'auto',
                  animation: `${pulse} 2s ease-in-out infinite`,
                  filter: 'drop-shadow(0 0 20px rgba(255, 0, 0, 0.3))',
                  mb: 4
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  color: '#ff0000',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textShadow: '0 0 10px rgba(255, 0, 0, 0.5)',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                  mb: 2,
                  animation: `${fadeIn} 1s ease-out`
                }}
              >
                Eyeballs on Screams!
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  color: '#ffffff',
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                  fontSize: { xs: '1.3rem', sm: '1.7rem', md: '2.2rem' },
                  mb: 2,
                  animation: `${fadeIn} 1s ease-out 0.2s both`
                }}
              >
                Supporting Indie Horror
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.3rem' },
                  mb: 3,
                  animation: `${fadeIn} 1s ease-out 0.3s both`
                }}
              >
                Starting at $2.99. Cancel anytime.
              </Typography>

              <Box 
                component="form" 
                onSubmit={handleSubmit}
                sx={{ 
                  maxWidth: '500px',
                  margin: '0 auto',
                  animation: `${fadeIn} 1s ease-out 0.6s both`
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  px: 2
                }}>
                  <TextField
                    placeholder="Email address"
                    variant="outlined"
                    type="email"
                    required
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      flex: { xs: '1 1 100%', sm: '1 1 auto' },
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        '& fieldset': {
                          borderColor: 'rgba(255, 0, 0, 0.5)',
                          borderWidth: 2
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 0, 0, 0.8)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                  <Button 
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{ 
                      bgcolor: '#e50914',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#f40612'
                      },
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { sm: '160px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ marginRight: '12px' }}>Get Started</span>
                    <ChevronRightIcon sx={{ fontSize: '1.8rem' }} />
                  </Button>
                </Box>
              </Box>
              
              {/* App Store Badges */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 3 },
                mt: 4,
                animation: `${fadeIn} 1s ease-out 0.8s both`
              }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box 
                      component="a"
                      href="https://apps.apple.com/us/app/screamify/id6738323226"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Box 
                        component="img"
                        src={iosBadge}
                        alt="Download on the App Store"
                        sx={{
                          height: { xs: '40px', sm: '48px' },
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box 
                      component="a"
                      href="https://play.google.com/store/apps/details?id=com.screamify.app&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Box 
                        component="img"
                        src={googlePlayBadge}
                        alt="Get it on Google Play"
                        sx={{
                          height: { xs: '40px', sm: '48px' },
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
              
              {/* App Hands Image */}
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                mt: 5,
                mb: 2,
                animation: `${fadeIn} 1s ease-out 1s both`
              }}>
                <Box 
                  component="img"
                  src={appHands}
                  alt="Screamify App Experience"
                  sx={{
                    maxWidth: '100%',
                    width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
                    height: 'auto',
                    filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5))',
                    borderRadius: '12px',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    }
                  }}
                />
              </Box>
            </Box>
          </Container>

          {/* Devices Gallery Section */}
          <Container maxWidth="xl">
            <Box sx={{ position: 'relative', zIndex: 1, animation: `${fadeIn} 1s ease-out 0.4s both`, mb: 5 }}>
              <Typography
                variant="h2"
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.9rem' },
                  textShadow: '0 0 10px rgba(255, 0, 0, 0.7)',
                  letterSpacing: '0.02em',
                  textAlign: 'center',
                  mb: 3
                }}
              >
                Available on all your devices
              </Typography>
              <Grid container spacing={2}>
                {[device1, device2, device3, device4, device5, device6].map((img, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Box
                      component="img"
                      src={img}
                      alt={`Screamify devices ${idx + 1}`}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 2,
                        display: 'block',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>

          {/* Features Section */}
          <Container maxWidth="xl">
            <Box sx={{ py: 8, position: 'relative', zIndex: 1 }}>
              <Grid container spacing={4}>
                {features.map((feature, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card
                      sx={{
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        border: '1px solid rgba(255, 0, 0, 0.3)',
                        borderRadius: 2,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 5px 15px rgba(255, 0, 0, 0.2)',
                          borderColor: 'rgba(255, 0, 0, 0.5)',
                        },
                        animation: `${fadeIn} 1s ease-out ${0.2 * index}s both`
                      }}
                    >
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                        <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                          {feature.title}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>

          {/* Latest from the Blog Section */}
          <Container maxWidth="xl">
            <Box 
              sx={{ 
                py: 8, 
                position: 'relative', 
                zIndex: 1,
                textAlign: 'center',
                animation: `${fadeIn} 1s ease-out`
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  color: '#ff0000',
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                  textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
                }}
              >
                Latest from the Blog
              </Typography>
              
              <Typography
                variant="h3"
                sx={{
                  color: 'white',
                  fontWeight: 300,
                  mb: 5,
                  fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                  maxWidth: '800px',
                  mx: 'auto'
                }}
              >
                Discover new horror insights, indie film reviews, and industry trends
              </Typography>
              
              {blogLoading ? (
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  {[1, 2, 3].map((item) => (
                    <Grid item xs={12} md={4} key={item}>
                      <Card
                        sx={{
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                          borderRadius: 2,
                          height: '100%',
                          border: '1px solid rgba(255, 0, 0, 0.2)',
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            paddingTop: '56.25%',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            position: 'relative'
                          }}
                        />
                        <CardContent>
                          <Box 
                            sx={{
                              height: '24px',
                              width: '80%',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              mb: 2
                            }}
                          />
                          <Box 
                            sx={{
                              height: '16px',
                              width: '30%',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              mb: 2
                            }}
                          />
                          <Box 
                            sx={{
                              height: '16px',
                              width: '100%',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              mb: 1
                            }}
                          />
                          <Box 
                            sx={{
                              height: '16px',
                              width: '90%',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  {blogPosts.map((post, index) => {
                    if (!post || !post.fields) return null;
                    
                    return (
                      <Grid item xs={12} md={4} key={post.id || index}>
                        <Card
                          component={Link}
                          to={`/blog/${post.fields.BlogId || ''}`}
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            border: '1px solid rgba(255, 0, 0, 0.3)',
                            borderRadius: 2,
                            height: '100%',
                            transition: 'all 0.3s ease',
                            textDecoration: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 5px 15px rgba(255, 0, 0, 0.2)',
                              borderColor: 'rgba(255, 0, 0, 0.5)',
                            },
                            animation: `${fadeIn} 1s ease-out ${0.2 * index}s both`
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              paddingTop: '56.25%',
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src={post.fields.Pic1Url || ''}
                              alt={post.fields.Title || 'Blog post'}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                              }}
                            />
                          </Box>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                component="span"
                                sx={{
                                  color: '#666666',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  fontSize: '0.8rem',
                                  letterSpacing: '1px',
                                  display: 'inline-block',
                                  mr: 1
                                }}
                              >
                                {post.fields.Category || 'HORROR'}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  fontSize: '0.8rem',
                                  fontWeight: 400,
                                }}
                              >
                                / {formatDate(post.fields.Date || '')}
                              </Typography>
                            </Box>
                            <Typography
                              variant="h5"
                              sx={{
                                color: 'white',
                                mb: 1,
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {post.fields.Title || 'Blog Post'}
                            </Typography>
                            <Typography
                              sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.9rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                mb: 2,
                                flexGrow: 1
                              }}
                            >
                              {(post.fields.Content ? stripMarkdown(post.fields.Content).substring(0, 120) : 'No content')}...
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: '#ff0000',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                mt: 'auto'
                              }}
                            >
                              Read More <ArrowForwardIcon sx={{ ml: 1, fontSize: '0.9rem' }} />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              
              <Button
                variant="outlined"
                component={Link}
                to="/blog"
                sx={{
                  color: 'white', 
                  borderColor: 'rgba(255, 0, 0, 0.5)',
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: '#ff0000',
                    backgroundColor: 'rgba(255, 0, 0, 0.1)'
                  },
                  mb: 2
                }}
              >
                View All Articles
              </Button>
            </Box>
          </Container>

          {/* FAQ Section */}
          <Container maxWidth="md">
            <Box sx={{ py: 8, position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h2"
                sx={{
                  color: '#ff0000',
                  fontWeight: 600,
                  mb: 4,
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                  textAlign: 'center',
                  textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
                }}
              >
                Frequently Asked Questions
              </Typography>

              <Box sx={{ mt: 4 }}>
                {faqs.map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      borderBottom: '1px solid rgba(255, 0, 0, 0.2)',
                      '&:before': {
                        display: 'none',
                      },
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                      animation: `${fadeIn} 1s ease-out ${0.2 * index}s both`,
                      '&:focus': {
                        outline: 'none',
                      },
                      '&.Mui-focused': {
                        outline: 'none',
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: '#ff0000' }} />}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(255, 0, 0, 0.1)',
                        },
                        '&:focus': {
                          outline: 'none',
                        },
                        '&.Mui-focused': {
                          outline: 'none',
                        }
                      }}
                    >
                      <Typography sx={{ fontWeight: 500, color: 'white' }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.8 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
              
              <Box 
                sx={{ 
                  maxWidth: '500px',
                  margin: '0 auto',
                  mt: 6,
                  animation: `${fadeIn} 1s ease-out 0.6s both`
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    color: 'white',
                    fontWeight: 300,
                    mb: 3,
                    fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                    textAlign: 'center'
                  }}
                >
                  Ready to join our horror community?
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  px: 2
                }}>
                  <TextField
                    placeholder="Email address"
                    variant="outlined"
                    type="email"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      flex: { xs: '1 1 100%', sm: '1 1 auto' },
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        '& fieldset': {
                          borderColor: 'rgba(255, 0, 0, 0.5)',
                          borderWidth: 2
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 0, 0, 0.8)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                  <Button 
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{ 
                      bgcolor: '#e50914',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: '#f40612'
                      },
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { sm: '160px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ marginRight: '12px' }}>Get Started</span>
                    <ChevronRightIcon sx={{ fontSize: '1.8rem' }} />
                  </Button>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'white',
                    mt: 6,
                    mb: 4,
                    textAlign: 'center',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    px: 2
                  }}
                >
                  Questions? Email info@screamify.com
                </Typography>
              </Box>
            </Box>
          </Container>

          <Footer />
        </Box>
      </>
    </HelmetProvider>
  );
}

export default LandingPage;